import { useEffect, useState, useMemo, useRef } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AddIcon } from "@chakra-ui/icons";
import { deleteCompany, getCompanies, newCompany } from "@/services/companies-service";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  Input as ChakraInput,
  FormControl,
  FormLabel,
  Center,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import { Table, TableRowActions, Panel, PageHeading } from "@bemap/ui";
import { formatDate, ICompany, useAuth } from "@bemap/utils";
import { useTranslation } from "react-i18next";

const CompanyList = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const initialCompany: ICompany = {
    team_id: parseInt(`${user?.currentTeamId}`),
    name: "",
    number_id: ""
  };

  // STATES
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "new" | "delete">();
  const [companies, setCompanys] = useState<ICompany[]>([]);
  const [company, setCompany] = useState<ICompany>(initialCompany);
  const [updateData, setUpdateData] = useState<string>("");

  const columns = useMemo<ColumnDef<ICompany, any>[]>(
    () => [
      {
        accessorFn: (row) => row.name,
        id: "Nome",
        header: t('analyst.companies_table_name'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.number_id,
        id: "CNPJ",
        header: t('analyst.companies_table_cnpj'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.created_at,
        id: "Criado em",
        header: t('analyst.companies_table_created'),
        cell: (info) => formatDate(info.getValue()),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('table.actions'),
        cell: (row) => {
          return (
            <TableRowActions
              viewAction={() => navigate(`${row.getValue().id}`)}
            />
          );
        },
      },
    ],
    [t, navigate]
  );

  const background = useColorModeValue("transparent", "transparent");
  const textColor = useColorModeValue("#1A202C", "alphaWhite.600");

  useEffect(() => {
    loadCompanys();
  }, [updateData]);

  function closeModal() {
    onClose();
    setCompany(initialCompany);
  }

  async function loadCompanys() {
    setIsLoading(true);
    try {
      const response = await getCompanies();
      setCompanys(response.message);
      setIsLoading(false);
    } catch (error) {
      setCompanys([]);
      setIsLoading(false);
    }
  }

  async function createNewCompany() {
    try {
      setIsLoading(true);
      setIsSaving(true);
      toast({
        title: t('general.saving'),
        status: 'loading',
        isClosable: true,
      });
      await newCompany(company);
      toast.closeAll();
      setIsLoading(false);
      setIsSaving(false);
      closeModal();
      setUpdateData(new Date().toLocaleString());
      setCompany(initialCompany);
    } catch (error) {
      toast.closeAll();
      toast({
        title: t('general.error'),
        status: 'error',
        isClosable: true,
      });
      setIsLoading(false);
      setIsSaving(false);
    }
  }

  const handleDeleteCompany = async () => {
    try {
      toast({
        title: t('general.deleting'),
        status: 'loading',
        isClosable: true,
      });
      if (company.id) {
        await deleteCompany(company.id);
        setUpdateData(new Date().toLocaleString());
        closeModal();
      }
    } catch (error) {
      toast.closeAll();
      toast({
        title: t('general.error'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  async function handleChange(event: any) {
    setCompany({
      ...company,
      [event.target.name]: event.target.value,
    });
  }

  function RenderModal() {
    switch (modalType) {
      case "new":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.companies_new')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"}>
                  <FormControl>
                    <FormLabel>{t('analyst.companies_table_name')}</FormLabel>
                    <ChakraInput
                      color={textColor}
                      background={background}
                      name="name"
                      placeholder="Ex: BeMap"
                      onChange={handleChange}
                      value={company.name}
                      required
                    />
                  </FormControl>
                  <FormControl mt={4}>
                    <FormLabel>{t('analyst.companies_table_cnpj')}</FormLabel>
                    <ChakraInput
                      color={textColor}
                      background={background}
                      name="number_id"
                      placeholder="Ex: 55.555.555/0001-55"
                      onChange={handleChange}
                      value={company.number_id}
                      required
                    />
                  </FormControl>
                </Flex>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={createNewCompany} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "edit":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.companies_edit')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"}>
                  <FormControl>
                    <FormLabel>{t('analyst.companies_table_name')}</FormLabel>
                    <ChakraInput
                      color={textColor}
                      background={background}
                      name="name"
                      placeholder="Ex: BeMap"
                      onChange={handleChange}
                      value={company.name}
                      required
                    />
                  </FormControl>
                  <FormControl mt={4}>
                    <FormLabel>{t('analyst.companies_table_cnpj')}</FormLabel>
                    <ChakraInput
                      color={textColor}
                      background={background}
                      name="number_id"
                      placeholder="Ex: 55.555.555/0001-55"
                      onChange={handleChange}
                      value={company.number_id}
                      required
                    />
                  </FormControl>
                </Flex>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={createNewCompany} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('analyst.companies_delete')}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('actions.delete_confirm')}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel')}
                  </Button>
                  <Button colorScheme='red' onClick={handleDeleteCompany} ml={3}>
                    {t('actions.delete_confirm_button')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        );
    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <VStack gap={4} align="stretch" w="100%">
        <PageHeading title={t('analyst.companies')} actions={[
          {
            label: t('analyst.companies_new'),
            icon: <AddIcon />,
            onClick: () => {
              onOpen();
              setModalType("new");
            }
          }
        ]} />

        <Panel>
          <Table
            tableId="clients-companies-table"
            columns={columns}
            data={companies}
          />
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default CompanyList; 